/**
 * Auto-selects the realtime vehicle matching the in-progress transit leg of the
 * displayed itinerary, and overrides its interpolated position delay with the
 * leg's enriched realtime corrections (SIRI / search.ch / GTFS-RT).
 *
 * Lifecycle:
 * - `now` inside a transit leg window (`from.time` → `to.time`): resolve
 *   `providerId` → realtime `trip_id`, hydrate the cache when the trip is outside
 *   the polled viewport, select the vehicle (with camera follow), apply delay override.
 * - Window exit or itinerary change: deselect the vehicle we selected and clear
 *   the override so the GTFS-RT delay applies again.
 * - A manual vehicle selection always wins: auto-select pauses ("blocked") until
 *   the user deselects, then resumes. An explicit deselect of the auto-selected
 *   vehicle is remembered ("dismissed") until the leg ends.
 */

import { useEffect, useRef } from "react";

import { type RealtimeTransitPathSelection } from "../components/MapLibre/vehicleSelection/vehicleSelection.types";
import { VEHICLE } from "../config/global.configuration";
import { type VehicleManager } from "../managers/VehicleManager";
import { type RealtimeCache } from "../model/RealtimeCache";
import { realtimeService } from "../services/RealtimeService";
import { ephemeralStore, navigationSessionSlice } from "../store/ephemeral";
import {
    buildTripDelayWireFromLeg,
    findActiveTransitLeg,
    resolveRealtimeTripIdFromProviderId,
} from "../tools/itineraryVehicleAutoSelect.tools";
import type { ItineraryDTO, ItineraryLegDTO } from "../types/dtos/api/itinerary/itinerary.dto";

interface UseItineraryVehicleAutoSelectProps {

    /** Itinerary currently drawn on the map. */
    "itinerary": ItineraryDTO | null | undefined;

    /** Vehicle selection state owned by the map component. */
    "selectedVehicle": RealtimeTransitPathSelection | null;

    /** Vehicle manager (delay overrides + vehicle lookup). */
    "vehicleManager": VehicleManager;

    /** Shared realtime cache (trip index, schedules, timelines). */
    "realtimeCache": RealtimeCache;

    /**
     * Selects a vehicle by realtime `trip_id` (map layer + follow mode).
     * Returns `true` when the vehicle exists and was selected.
     */
    "selectVehicle": ( tripId: string ) => boolean;

    /** Clears the active vehicle selection (layer + React state). */
    "clearVehicleSelection": () => void;
}

/** Auto-select tracking phases. */
type TrackedPhase = "selected" | "dismissed" | "blocked";

interface TrackedState {
    "legId":  string;
    "tripId": string;
    "phase":  TrackedPhase;

    /** Leg destination stop (`leg.to.stop`), used to truncate the realtime path. */
    "destinationStopId":     string | null;
    "destinationStopParent": string | null;
}

/**
 * Orchestrates itinerary-driven vehicle auto-selection.
 *
 * @param props Hook configuration.
 */
export function useItineraryVehicleAutoSelect( {
    itinerary,
    selectedVehicle,
    vehicleManager,
    realtimeCache,
    selectVehicle,
    clearVehicleSelection,
}: UseItineraryVehicleAutoSelectProps ): void {
    const trackedRef = useRef<TrackedState | null>( null );
    const selectedVehicleRef = useRef<RealtimeTransitPathSelection | null>( selectedVehicle );
    const evaluationSeqRef = useRef( 0 );
    const selectVehicleRef = useRef( selectVehicle );
    const clearVehicleSelectionRef = useRef( clearVehicleSelection );

    /**
     * Publishes the leg currently replaced by realtime mode so the itinerary
     * renderer can hide its static segment (covers every resolution path).
     */
    const publishRealtimeLegClaimRef = useRef( () => {
        const tracked = trackedRef.current;
        const claim = tracked !== null &&
          tracked.phase === "selected" &&
          selectedVehicleRef.current?.tripId === tracked.tripId
            ? tracked.legId
            : null;
        const trackedTripId = tracked?.tripId ?? null;
        const destinationStopId = claim !== null && tracked !== null
            ? tracked.destinationStopId
            : null;
        const destinationStopParent = claim !== null && tracked !== null
            ? tracked.destinationStopParent
            : null;

        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
            if ( previous.realtimeItineraryLegId === claim &&
              previous.trackedItineraryTripId === trackedTripId &&
              previous.realtimeItineraryDestinationStopId === destinationStopId &&
              previous.realtimeItineraryDestinationStopParent === destinationStopParent ) {
                return previous;
            }

            return {
                ...previous,
                "realtimeItineraryLegId":                 claim,
                "trackedItineraryTripId":                 trackedTripId,
                "realtimeItineraryDestinationStopId":     destinationStopId,
                "realtimeItineraryDestinationStopParent": destinationStopParent,
            };
        } );
    } );

    /**
     * Publishes whether the active leg is still being resolved. During that
     * window the itinerary renderer may optimistically hide the leg segment.
     */
    const setRealtimeLegPendingRef = useRef( ( pending: boolean ): void => {
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
            if ( previous.realtimeItineraryLegPending === pending ) {
                return previous;
            }

            return {
                ...previous,
                "realtimeItineraryLegPending": pending,
            };
        } );
    } );

    useEffect( () => {
        selectVehicleRef.current = selectVehicle;
        clearVehicleSelectionRef.current = clearVehicleSelection;
    }, [ selectVehicle, clearVehicleSelection ] );

    /*
     * Tracks user intent around our own selection:
     * - selection cleared while "selected" → explicit dismiss, do not re-select for this leg;
     * - selection cleared while "blocked" → manual selection released, auto-select resumes;
     * - another vehicle selected while "selected" → manual wins, pause until deselect.
     */
    useEffect( () => {
        const tracked = trackedRef.current;
        selectedVehicleRef.current = selectedVehicle;

        if ( tracked === null ) {
            return;
        }

        if ( selectedVehicle === null ) {
            if ( tracked.phase === "selected" ) {
                trackedRef.current = {
                    ...tracked,
                    "phase": "dismissed",
                };
                vehicleManager.setDelayOverrideForTrip( tracked.tripId, null );
            } else if ( tracked.phase === "blocked" ) {
                trackedRef.current = null;
            }
        } else if ( tracked.phase === "selected" && selectedVehicle.tripId !== tracked.tripId ) {
            trackedRef.current = {
                ...tracked,
                "phase": "blocked",
            };
            vehicleManager.setDelayOverrideForTrip( tracked.tripId, null );
        } else if ( tracked.phase === "blocked" && selectedVehicle.tripId === tracked.tripId ) {
            trackedRef.current = {
                ...tracked,
                "phase": "selected",
            };
        }

        publishRealtimeLegClaimRef.current();
    }, [ selectedVehicle, vehicleManager ] );

    useEffect( () => {
        const setRealtimeLegPending = setRealtimeLegPendingRef.current;

        trackedRef.current = null;
        publishRealtimeLegClaimRef.current();
        setRealtimeLegPending( false );

        if ( itinerary === null || itinerary === undefined || !VEHICLE.itineraryAutoSelect.enabled ) {
            return;
        }

        /**
         * Drops the tracked state: deselects the vehicle only when the current
         * selection is the one we made, then clears the delay override.
         */
        const resetTracked = (): void => {
            const tracked = trackedRef.current;
            if ( tracked === null ) {
                return;
            }

            if (
                tracked.phase === "selected" &&
                selectedVehicleRef.current?.tripId === tracked.tripId
            ) {
                clearVehicleSelectionRef.current();
            }

            vehicleManager.setDelayOverrideForTrip( tracked.tripId, null );
            trackedRef.current = null;
            publishRealtimeLegClaimRef.current();
        };

        /**
         * Applies the leg's corrected delays as the vehicle's effective delay.
         */
        const applyDelayOverride = ( leg: ItineraryLegDTO, tripId: string ): void => {
            const scheduleStops = realtimeCache.schedules.get( tripId )?.data.stops ?? [];
            const wire = buildTripDelayWireFromLeg( leg, tripId, scheduleStops );
            vehicleManager.setDelayOverrideForTrip( tripId, wire );
        };

        /**
         * Resolves `providerId` → realtime `trip_id` (index hit, then `/delays` fallback
         * which resolves fuzzy ids when the trip carries a delay entry).
         */
        const resolveTripId = async( providerId: string ): Promise<string | null> => {
            const fromIndex = resolveRealtimeTripIdFromProviderId( providerId, realtimeCache.tripIndex );
            if ( fromIndex !== null ) {
                return fromIndex;
            }

            try {
                const delays = await realtimeService.fetchDelays( [ providerId ] );

                return delays[0]?.trip_id ?? null;
            } catch {
                return null;
            }
        };

        /**
         * Hydrates cache + vehicle manager for a trip outside the polled viewport.
         */
        const ensureTripHydrated = async( tripId: string ): Promise<boolean> => {
            if ( realtimeCache.timelines.has( tripId ) ) {
                return true;
            }

            try {
                const detail = await realtimeService.fetchTripDetail( tripId );
                if ( !realtimeCache.upsertTripDetail( detail ) ) {
                    return false;
                }

                vehicleManager.onDataUpdate();

                return true;
            } catch {
                return false;
            }
        };

        const evaluate = async(): Promise<void> => {
            const seq = evaluationSeqRef.current;
            const leg = findActiveTransitLeg( itinerary, Date.now() );

            if ( leg === null ) {
                resetTracked();
                setRealtimeLegPending( false );

                return;
            }

            const tracked = trackedRef.current;
            if ( tracked !== null && tracked.legId !== leg.id ) {
                resetTracked();
            } else if ( tracked !== null && tracked.phase === "dismissed" ) {
                // Explicitly dismissed on this leg: do not re-select until it ends.
                setRealtimeLegPending( false );

                return;
            } else if (
                tracked !== null &&
                tracked.phase === "blocked" &&
                selectedVehicleRef.current !== null
            ) {
                // A different vehicle is manually selected: manual wins.
                setRealtimeLegPending( false );

                return;
            }

            const providerId = leg.providerId?.trim() ?? "";
            if ( providerId === "" ) {
                setRealtimeLegPending( false );

                return;
            }

            setRealtimeLegPending( true );
            const resolvedTripId = await resolveTripId( providerId );
            if ( resolvedTripId === null || seq !== evaluationSeqRef.current ) {
                setRealtimeLegPending( false );

                return;
            }

            const currentSelection = selectedVehicleRef.current;
            if ( currentSelection !== null && currentSelection.tripId !== resolvedTripId ) {
                trackedRef.current = {
                    "legId":                 leg.id,
                    "tripId":                resolvedTripId,
                    "phase":                 "blocked",
                    "destinationStopId":     leg.to?.stop?.id?.trim() ?? null,
                    "destinationStopParent": leg.to?.stop?.parent?.trim() ?? null,
                };
                setRealtimeLegPending( false );
                publishRealtimeLegClaimRef.current();

                return;
            }

            if ( currentSelection === null ) {
                if ( !await ensureTripHydrated( resolvedTripId ) || seq !== evaluationSeqRef.current ) {
                    setRealtimeLegPending( false );

                    return;
                }

                if ( !selectVehicleRef.current( resolvedTripId ) ) {
                    setRealtimeLegPending( false );

                    return;
                }
            }

            trackedRef.current = {
                "legId":                 leg.id,
                "tripId":                resolvedTripId,
                "phase":                 "selected",
                "destinationStopId":     leg.to?.stop?.id?.trim() ?? null,
                "destinationStopParent": leg.to?.stop?.parent?.trim() ?? null,
            };
            applyDelayOverride( leg, resolvedTripId );
            setRealtimeLegPending( false );
            publishRealtimeLegClaimRef.current();
        };

        void evaluate();
        const interval = setInterval( () => {
            void evaluate();
        }, VEHICLE.itineraryAutoSelect.tickMs );

        return () => {
            clearInterval( interval );
            evaluationSeqRef.current += 1;
            resetTracked();
            setRealtimeLegPending( false );
        };
    }, [ itinerary, vehicleManager, realtimeCache ] );
}
